import { useEffect } from 'react';
import type { AssistantRuntime } from '@assistant-ui/react';
import type { PlatformEvent, ConversationMessageAddedData } from '../types/events';
import type { UIEvent } from '../types/ui-events';
import { usePlatformEvents } from '../contexts/PlatformEventContext';
import { signalInjectedUserTurn } from '../components/assistant-ui/injected-turn-signal';
import { useLatest } from './useLatest';
import {
  createRunStreamState,
  applyRunEvent,
  buildRunContent,
  type RunStreamState,
} from '../utils/run-stream-state';
import { playCueOnce } from '../utils/audio-theme';
import { isRunCancelled, type ActiveRunIdStore } from './useAsyncInvokeAdapter';

/**
 * Subscribes to background agent run events (runs NOT initiated by the
 * current adapter) via WebSocket and renders them as assistant messages
 * in the conversation thread.
 *
 * Used by every conversation surface (Builder, Sandbox, Conversations)
 * so AUI action dispatches and other server-initiated runs appear in
 * the thread live, not just on reload.
 *
 * Internally accumulates per-runId state via the same `applyRunEvent`
 * reducer the assistant-ui adapter uses, so background runs render with
 * exactly the same content (reasoning, tool calls, sub-agents, final
 * answer) as composer-typed messages.
 *
 * IMPORTANT: This hook MUST only be active for the surface whose
 * runtime is currently mounted in a visible `<Thread.Root>`. The Builder,
 * Sandbox, and Conversations surfaces are persistently mounted in
 * `AppLayout` and only show one at a time — pass `enabled = visible` so
 * the inactive surfaces don't try to append to detached runtimes (which
 * would throw "empty thread placeholder" errors) AND don't cause every
 * background run to fan out into all three surfaces simultaneously.
 *
 * The hook also filters events to ones whose `conversationId` matches the
 * current surface's runtime, so a background run for some unrelated
 * conversation never lands in the wrong thread.
 *
 * Coordination with the foreground run: when this surface has a
 * foreground run in flight (`activeRunIdStore.get()` is defined),
 * background-run terminal appends are queued and drained FIFO once the
 * foreground run finishes. This preserves visual ordering — the user's
 * prompt and its assistant response stay adjacent, with background-run
 * results appended after — instead of a background run's terminal
 * message wedging itself between the user message and the still-streaming
 * foreground response.
 */

interface PendingAppend {
  role: 'user' | 'assistant';
  content: ReturnType<typeof buildRunContent>;
  customMeta: Record<string, unknown>;
}

/**
 * Convert persisted message content blocks (`[{ text }]`) to assistant-ui text
 * parts, matching the conversation-restore converter so a live-injected turn and
 * the same turn after reload render identically. Non-text blocks are skipped —
 * the only server-injected user turns today are text answers.
 */
function userTextParts(content: unknown): Array<{ type: 'text'; text: string }> {
  if (!Array.isArray(content)) return [];
  const parts: Array<{ type: 'text'; text: string }> = [];
  for (const block of content) {
    const text = (block as { text?: unknown })?.text;
    if (typeof text === 'string' && text.length > 0) parts.push({ type: 'text', text });
  }
  return parts;
}

export function useAgentRunEvents(
  runtime: AssistantRuntime,
  activeRunIdStore: ActiveRunIdStore,
  conversationIdRef: React.RefObject<string | undefined>,
  dispatchUIEvent?: (event: UIEvent) => void,
  getToolName?: (nameOrId: string) => string,
  enabled: boolean = true,
  // Reactive conversation id used to scope the subscription server-side. When
  // undefined (e.g. a brand-new chat before its conversation exists) the
  // subscription stays unscoped and relies on the per-event `conversationIdRef`
  // gating below — same as before this hook scoped server-side.
  conversationId?: string,
) {
  const { subscribe } = usePlatformEvents();

  const dispatchRef = useLatest(dispatchUIEvent);
  const getToolNameRef = useLatest(getToolName);
  const runtimeRef = useLatest(runtime);

  useEffect(() => {
    if (!enabled) return;

    // Per-run reducer state. `appended` marks that this turn's content has
    // already been injected into the thread — the entry is RETAINED after a
    // terminal (not deleted) because ask_user parks emit a trailing
    // completed/final_answer tail for the same runId, and the warm claude
    // socket reuses one runId across turns (a fresh `started` resets it).
    // Deleting on terminal made a later ask on the same/unseen runId hit a
    // missing-state guard and vanish (prod incident 2026-07-08).
    const stateByRunId = new Map<string, { state: RunStreamState; appended: boolean }>();
    const pendingAppends: PendingAppend[] = [];

    const flushPending = () => {
      if (pendingAppends.length === 0) return;
      const rt = runtimeRef.current;
      if (!rt) return;
      while (pendingAppends.length > 0) {
        const next = pendingAppends.shift()!;
        rt.thread.append({
          role: next.role,
          content: next.content,
          startRun: false,
          ...(Object.keys(next.customMeta).length > 0
            ? { metadata: { custom: next.customMeta } }
            : {}),
        });
      }
    };

    const enqueueOrAppend = (
      role: PendingAppend['role'],
      content: PendingAppend['content'],
      customMeta: PendingAppend['customMeta'],
    ) => {
      if (activeRunIdStore.get() !== undefined) {
        // Foreground run is mid-stream — defer to preserve thread ordering.
        pendingAppends.push({ role, content, customMeta });
        return;
      }
      const rt = runtimeRef.current;
      if (!rt) return;
      rt.thread.append({
        role,
        content,
        startRun: false,
        ...(Object.keys(customMeta).length > 0
          ? { metadata: { custom: customMeta } }
          : {}),
      });
    };

    // Runs the foreground adapter owns/owned. Trailing fan-out copies of a
    // foreground run's events can arrive AFTER the adapter cleared the active
    // run id (it clears on terminal packaging) — without this, the lazy state
    // creation below would rebuild them into a duplicate assistant turn.
    const foregroundRunIds = new Set<string>();

    const handler = (event: PlatformEvent) => {
      // Skip events for runs the adapter is already handling (composer-typed
      // messages). Those are streamed live by the adapter itself.
      if (event.subjectId && event.subjectId === activeRunIdStore.get()) {
        foregroundRunIds.add(event.subjectId);
        return;
      }

      const runId = event.subjectId;
      if (!runId) return;

      // A fresh `started` on a previously-foreground runId means the warm
      // socket reused the id for a new turn this window is NOT driving — let
      // it stream as a background run again.
      if (event.eventType === 'agent.run.started') {
        foregroundRunIds.delete(runId);
      } else if (foregroundRunIds.has(runId)) {
        // Trailing fan-out copy of a foreground-owned run — already rendered.
        return;
      }

      // A run the user cancelled keeps emitting events server-side; never
      // re-render it here after the foreground tore down its exchange.
      if (isRunCancelled(runId)) {
        stateByRunId.delete(runId);
        return;
      }

      // Strict conversation scoping: only inject a background run that
      // DEFINITIVELY belongs to THIS surface's conversation. Drop the event
      // when this surface has no conversation yet (a brand-new chat), when the
      // event carries no conversationId, or when it belongs to a different
      // conversation. Anything looser leaks another agent's run into this
      // thread — the bug this guards against. (The server-side `conversationId`
      // filter scopes too, but this stays the authoritative gate for the
      // unscoped new-chat window and any event missing a conversationId.)
      const surfaceConversationId = conversationIdRef.current;
      const eventConversationId = (event.data as { conversationId?: string })?.conversationId;
      if (!surfaceConversationId || eventConversationId !== surfaceConversationId) {
        return;
      }

      // Lazily create state on ANY event for an unseen runId — not just
      // `started`. A run this window never saw start (server-created follow-up
      // run, or the warm socket reusing a runId whose prior turn already
      // finished) must still be able to surface — the incident case is a
      // later ask_user on such a run. A fresh `started` for an entry that
      // already finished/appended means the runId was reused for a NEW turn:
      // reset so the new turn streams from scratch.
      let entry = stateByRunId.get(runId);
      if (
        event.eventType === 'agent.run.started' &&
        entry &&
        (entry.appended || entry.state.terminal)
      ) {
        entry = undefined;
      }
      if (!entry) {
        entry = { state: createRunStreamState(), appended: false };
        stateByRunId.set(runId, entry);
      }
      const state = entry.state;

      // Fire the response cue as soon as the agent starts emitting its
      // final answer — earlier than terminal so playback (fetch + decode
      // + start) runs in parallel with the streamed text. Background runs
      // surface in the thread on terminal, so this is mostly cosmetic for
      // them, but keeps behavior consistent with foreground runs.
      if (event.eventType === 'agent.run.final_answer_start') {
        playCueOnce('thread.response', `run:${runId}`);
      }

      applyRunEvent(state, event, {
        dispatchUIEvent: dispatchRef.current,
        getToolName: getToolNameRef.current,
      });

      if (state.terminal && !entry.appended) {
        const t = state.terminal;
        const content = buildRunContent(state, {
          dispatchUIEvent: dispatchRef.current,
          getToolName: getToolNameRef.current,
        });

        // For failed runs, append an error part if there's no other content.
        if (t.kind === 'failed' && content.length === 0) {
          content.push({
            type: 'text',
            text: `Background run failed: ${t.error ?? 'unknown error'}`,
          });
        }

        if (content.length > 0) {
          // Audio fallback for legacy runs that never emit
          // final_answer_start. Live runs already fired on the start
          // event; playCueOnce dedupes by run id.
          if (t.kind === 'completed' && state.hasFinalAnswer) {
            playCueOnce('thread.response', `run:${t.runId ?? runId}`);
          }
          const customMeta: Record<string, unknown> = {
            ...(t.runId && { runId: t.runId }),
            ...(t.durationMs != null && { runDurationMs: t.durationMs }),
            ...(t.delegationRef && { delegationRef: t.delegationRef }),
          };
          enqueueOrAppend('assistant', content, customMeta);
        }
        // Mark appended but KEEP the entry: the parked/completed tail
        // (`completed`, `final_answer_*`) for this runId keeps mutating the
        // retained state without re-appending, and a reused runId resets on
        // its next `started`.
        entry.appended = true;
      }
    };

    // `conversation.message_added`: a user turn persisted server-side that this
    // window did NOT optimistically render — the canonical case is an inbox
    // ask-user answer dispatched as a follow-up run. We inject it as a user turn
    // so the open thread updates live; the run's assistant response arrives via
    // the `agent.run.*` handler above. Dedup by `runId` against our own
    // foreground run drops the composer / live-socket turns this also fires for.
    const messageHandler = (event: PlatformEvent) => {
      if (event.eventType !== 'conversation.message_added') return;
      const data = event.data as unknown as ConversationMessageAddedData;
      // Assistant turns stream via agent.run.* — never inject them here.
      if (data.role !== 'user') return;
      // Our own foreground / live-socket turn — already rendered optimistically.
      if (data.runId && data.runId === activeRunIdStore.get()) return;
      if (data.runId && isRunCancelled(data.runId)) return;
      // Strict conversation scoping (same gate as the run handler).
      const surfaceConversationId = conversationIdRef.current;
      if (!surfaceConversationId || data.conversationId !== surfaceConversationId) return;
      const parts = userTextParts(data.content);
      if (parts.length === 0) return;
      // Carry the ask_user link so an inbox-dispatched answer renders as the
      // structured "Answered" block live, not just plain text — and the
      // message source so platform-injected turns (event_notification)
      // render as a system-event chip instead of a user bubble.
      const customMeta = {
        ...(data.askUserRef ? { askUserRef: data.askUserRef } : {}),
        ...(data.source && data.source !== 'user' ? { source: data.source } : {}),
      };
      // Arm the viewport pin the way a composer send does (runStart never
      // fires for injected turns) — the injected chip pins to the top and the
      // follow-up response streams in below it.
      signalInjectedUserTurn(surfaceConversationId);
      enqueueOrAppend('user', parts as ReturnType<typeof buildRunContent>, customMeta);
    };

    // ── Hidden-tab buffering ─────────────────────────────────────────────
    // Never drive React commits (thread appends, streaming reducers) inside a
    // hidden tab. Safari suspends backgrounded pages, and rendering an entire
    // agent run into a suspended page is what wedges its WebContent process in
    // a black, input-dead state until reload (2026-08 credential-continuation
    // incident: OAuth completes in a child tab while this tab streams the
    // continuation run). Events queue while `document.hidden` and drain in
    // arrival order on the visibilitychange back to visible. If the buffer
    // overflows (a very long run against a small cap), replaying a truncated
    // stream would build a corrupt turn — drop the buffer and let the caller's
    // normal reload path (conversation refetch on focus/reconnect) recover.
    const HIDDEN_BUFFER_CAP = 2000;
    const hiddenBuffer: Array<{ fn: (e: PlatformEvent) => void; event: PlatformEvent }> = [];
    let hiddenOverflowed = false;

    const whenVisible =
      (fn: (e: PlatformEvent) => void) =>
      (event: PlatformEvent) => {
        if (typeof document !== 'undefined' && document.hidden) {
          if (hiddenBuffer.length >= HIDDEN_BUFFER_CAP) {
            hiddenBuffer.length = 0;
            hiddenOverflowed = true;
          }
          if (!hiddenOverflowed) hiddenBuffer.push({ fn, event });
          return;
        }
        fn(event);
      };

    const drainHiddenBuffer = () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      hiddenOverflowed = false;
      const queued = hiddenBuffer.splice(0);
      for (const item of queued) item.fn(item.event);
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', drainHiddenBuffer);
    }

    // Subscribe to this conversation's run events at full detail (token
    // streams). No runId filter — catch every (background) run in the
    // conversation. conversationId scopes server-side; the handler still gates
    // per-event as defense and to cover the unscoped new-chat window.
    const unsubscribeEvents = subscribe(
      [{ eventType: 'agent.run.*', level: 'full', ...(conversationId ? { conversationId } : {}) }],
      whenVisible(handler),
    );

    // Separate subscription for server-injected user turns. Scoped by
    // conversationId server-side; the handler re-gates per event.
    const unsubscribeMessages = subscribe(
      [{ eventType: 'conversation.message_added', ...(conversationId ? { conversationId } : {}) }],
      whenVisible(messageHandler),
    );

    // Drain pending appends whenever the foreground run goes idle.
    const unsubscribeActive = activeRunIdStore.subscribe(() => {
      if (activeRunIdStore.get() === undefined) flushPending();
    });

    return () => {
      unsubscribeEvents();
      unsubscribeMessages();
      unsubscribeActive();
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', drainHiddenBuffer);
      }
      hiddenBuffer.length = 0;
      stateByRunId.clear();
      // Drop any queued background appends — the surface is unmounting
      // (or conversation switching), so injecting them now would land in
      // the wrong thread.
      pendingAppends.length = 0;
    };
  }, [enabled, subscribe, activeRunIdStore, conversationIdRef, conversationId, dispatchRef, getToolNameRef, runtimeRef]);
}
